/**
 * File: SearchButton.js
 * Origin: isotect
 * Description: SearchButton component used to toggle a searchbar.
 *
 * Version: v1.0.0
 * Created: 2026-07-16
 * Last Updated: 2026-07-21
 *
 * Used By: isotect
 * Maintainer: Zachary Dicks
 *
 * Notes:
 * - Changes in this file may affect all consuming frontend modules.
 * - See Git repository 'isotect' for full change history.
 */

// Third-party packages
import { useEffect, useState, useRef } from "react";

// Local project files
import { IconButton } from "../ui/Buttons";
import SearchBar from "../forms/SearchBar";

/**
 * Renders a button that opens and closes a search bar.
 *
 * @component
 *
 * @param {Object} props - Component props
 * @param {Object} props.schema - Configuration schema
 * @param {string} props.query - Current search query
 * @param {(query: string) => void} props.setQuery - Callback that updates the search query
 *
 * @returns {JSX.Element} A search button and its associated search bar
 */
function SearchButton({ schema, query, setQuery }) {
    const [isOpen, setIsOpen] = useState(false);

    const ref = useRef(null);

    useEffect(() => {
        if (!isOpen) return;

        document.addEventListener("pointerdown", handleExternalClick);

        return () => {
            document.removeEventListener("pointerdown", handleExternalClick);
        };
    }, [isOpen, query]);

    const handleButtonClick = () => {
        setIsOpen(true);
    };

    const handleExternalClick = (event) => {
        if (
            ref.current &&
            !ref.current.contains(event.target) &&
            !query?.trim()
        ) {
            setIsOpen(false);
        }
    };

    return (
        <div className="search-button" ref={ref}>
            {!isOpen && <IconButton {...schema} onClick={handleButtonClick} />}
            {isOpen && <SearchBar query={query} setQuery={setQuery} />}
        </div>
    );
}

export default SearchButton;
